import * as React from "react"

import { cn } from "@/lib/utils"

// NEEC POS numeric-entry standard (applies to every <Input type="number">):
//  - keyboard row + numpad digits, Backspace, Tab, arrows work natively
//  - 'e', 'E', '+' never accepted; '-' only with allowNegative; '.'/',' only when decimals allowed
//  - `integer` (or step="1") = whole numbers only
//  - mouse-wheel never changes the value; pasted text is sanitised
//  - Enter never implicitly submits unless the field is inside a <form>
const BLOCKED = new Set(["e", "E", "+"]);

const Input = React.forwardRef(({ className, type, integer, allowNegative, onKeyDown, onPaste, onWheel, step, inputMode, ...props }, ref) => {
  const numeric = type === "number";
  const wholeOnly = numeric && (integer || step === "1" || step === 1);
  const guardKey = (e) => {
    if (numeric && !e.ctrlKey && !e.metaKey) {
      const k = e.key;
      if (BLOCKED.has(k) || (k === "-" && !allowNegative) || ((k === "." || k === ",") && wholeOnly)) e.preventDefault();
    }
    onKeyDown?.(e);
  };
  const guardPaste = (e) => {
    if (numeric) {
      const raw = (e.clipboardData?.getData("text") || "").trim();
      let clean = raw.replace(wholeOnly ? /[^0-9-]/g : /[^0-9.,-]/g, "").replace(/,/g, ".");
      if (!allowNegative) clean = clean.replace(/-/g, "");
      if (clean !== raw) {
        e.preventDefault();
        const el = e.target;
        const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
        setter.call(el, clean);
        el.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
    onPaste?.(e);
  };
  const guardWheel = (e) => {
    if (numeric && document.activeElement === e.target) e.target.blur();
    onWheel?.(e);
  };
  return (
    <input
      type={type}
      step={step}
      inputMode={inputMode || (numeric ? (wholeOnly ? "numeric" : "decimal") : undefined)}
      onKeyDown={guardKey}
      onPaste={guardPaste}
      onWheel={guardWheel}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
